import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Package, MapPin, Clock } from 'lucide-react';
import { getMyOrders } from '../lib/api';
import { useAuthStore } from '../lib/authStore';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    const load = async () => {
      if (!isAuthenticated) { setLoading(false); return; }
      try { const res = await getMyOrders(); setOrders(res.data || []); } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [isAuthenticated]);

  const statusLabels = { pendente: 'Pendente', confirmado: 'Confirmado', enviado: 'Enviado', entregue: 'Entregue', cancelado: 'Cancelado' };
  const statusColors = { pendente: 'bg-yellow-600', confirmado: 'bg-blue-600', enviado: 'bg-purple-600', entregue: 'bg-green-600', cancelado: 'bg-red-600' };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col bg-[#09090B]">
        <Header />
        <main className="flex-1 flex items-center justify-center py-16 pb-24 md:pb-16">
          <div className="text-center">
            <ShoppingBag className="h-20 w-20 text-zinc-700 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white uppercase tracking-tight mb-3" style={{ fontFamily: 'Teko, sans-serif' }}>Meus Pedidos</h2>
            <p className="text-zinc-500 mb-8">Faça login para ver seus pedidos</p>
            <Link to="/login" data-testid="login-for-orders" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-3 uppercase tracking-wider transition-all">Entrar</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#09090B]">
      <Header />
      <main className="flex-1 py-6 pb-24 md:pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6" style={{ fontFamily: 'Teko, sans-serif' }} data-testid="orders-title">Meus Pedidos</h1>

          {loading ? (
            <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="bg-zinc-900 border border-zinc-800 h-32 animate-pulse" />)}</div>
          ) : orders.length > 0 ? (
            <div className="space-y-4" data-testid="orders-list">
              {orders.map((o) => (
                <div key={o.id} className="bg-zinc-900 border border-zinc-800 p-5" data-testid={`order-${o.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <ShoppingBag className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="text-white font-bold text-sm">Pedido #{o.id?.slice(-8)}</p>
                        <p className="text-zinc-500 text-xs flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(o.created_at).toLocaleString('pt-BR')}</p>
                      </div>
                    </div>
                    <span className={`text-[10px] px-3 py-1 font-bold text-white uppercase ${statusColors[o.status] || 'bg-zinc-600'}`}>{statusLabels[o.status] || o.status}</span>
                  </div>

                  <div className="border-t border-zinc-800 pt-3 space-y-1">
                    {o.items?.map((item, j) => (
                      <div key={j} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-zinc-600" />
                          <span className="text-zinc-300">{item.quantidade}x {item.nome}</span>
                        </div>
                        <span className="text-zinc-400">R$ {(item.preco * item.quantidade).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-zinc-800 mt-3 pt-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex gap-4 text-xs">
                      <span className="text-zinc-500">{o.tipo_entrega === 'retirar' ? 'Retirar na loja' : 'Entrega em domicílio'}</span>
                      {o.distancia_km && <span className="text-zinc-500">{o.distancia_km} km</span>}
                      {o.coupon_code && <span className="text-green-400">Cupom: {o.coupon_code}</span>}
                    </div>
                    <div className="text-right">
                      {o.discount > 0 && <p className="text-green-400 text-xs">Desconto: -R$ {o.discount?.toFixed(2)}</p>}
                      {o.frete > 0 && <p className="text-zinc-500 text-xs">Frete: R$ {o.frete?.toFixed(2)}</p>}
                      <p className="text-red-500 font-bold text-lg">R$ {o.total?.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <ShoppingBag className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500 text-lg mb-4">Nenhum pedido ainda</p>
              <Link to="/produtos" className="text-red-500 hover:text-red-400 font-bold uppercase text-sm tracking-wider">Explorar Produtos</Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
